import React, { useEffect } from 'react';
import { Container, Nav } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCreditCard,
  faBell,
  faKey,
  faPlug,
  faLock,
  faBuildingColumns,
  faUser,
  faPalette,
  faChartLine,
  faShieldHalved,
  faFlask,
} from '@fortawesome/free-solid-svg-icons';
import { applyThemeMode, ThemeToggle, useThemeMode } from '../../design-system';
import UserProfile from '../UserProfile';
import BrokerConnectionsPage from '../BrokerConnections/BrokerConnectionsPage';
import ApiKeySettings from './ApiKeySettings/ApiKeySettings';
import SubscriptionManagement from './SubscriptionManagement/SubscriptionManagement';
import ConnectionSettings from './ConnectionSettings/ConnectionSettings';
import SavedEnvVarSets from './SavedEnvVarSets';
import NotificationSettings from './NotificationSettings/NotificationSettings';
import UsageSettings from './UsageSettings/UsageSettings';
import SecuritySettings from './SecuritySettings/SecuritySettings';
import ExperimentSettings from './ExperimentSettings/ExperimentSettings';
import { isExperimentPreviewAvailable } from '../../utils/experimentPreviewOverrides';
import './AccountSettings.css';

type SettingsSectionId =
  | 'profile'
  | 'api-keys'
  | 'subscriptions'
  | 'usage'
  | 'connections'
  | 'saved-env-vars'
  | 'broker-connections'
  | 'notifications'
  | 'appearance'
  | 'security'
  | 'experiments';

interface SettingsSection {
  id: SettingsSectionId;
  label: string;
  title: string;
  icon: IconDefinition;
  path: `/account-settings/${SettingsSectionId}`;
  component: React.ComponentType<Record<string, never>>;
  previewOnly?: boolean;
}

interface AccountSettingsProps {
  contextualNavigation?: boolean;
  experimentPreviewAvailable?: boolean;
}

const AppearanceSettings = (): React.ReactElement => (
  <div className="appearance-settings">
    <div className="settings-subpage-header appearance-settings-header">
      <div className="settings-subpage-header-left">
        <h2 className="settings-subpage-title">Appearance</h2>
        <p className="settings-subpage-description">Choose workspace theme.</p>
      </div>
    </div>

    <section className="appearance-settings-list" aria-label="Appearance preferences">
      <div className="appearance-settings-row">
        <div>
          <h5>Theme</h5>
          <p>
            Light mode is experimental. Your choice applies across the website; dark is the default.
          </p>
        </div>
        <ThemeToggle label="Website theme" />
      </div>
    </section>
  </div>
);

const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    id: 'profile',
    label: 'My Profile',
    title: 'My Profile',
    icon: faUser,
    path: '/account-settings/profile',
    component: UserProfile,
  },
  {
    id: 'appearance',
    label: 'Appearance',
    title: 'Appearance',
    icon: faPalette,
    path: '/account-settings/appearance',
    component: AppearanceSettings,
  },
  {
    id: 'security',
    label: 'Security',
    title: 'Security',
    icon: faShieldHalved,
    path: '/account-settings/security',
    component: SecuritySettings,
  },
  {
    id: 'api-keys',
    label: 'API Keys',
    title: 'API Keys',
    icon: faKey,
    path: '/account-settings/api-keys',
    component: ApiKeySettings,
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions & Billing',
    title: 'Subscriptions & Billing',
    icon: faCreditCard,
    path: '/account-settings/subscriptions',
    component: SubscriptionManagement,
  },
  {
    id: 'usage',
    label: 'Usage',
    title: 'Usage',
    icon: faChartLine,
    path: '/account-settings/usage',
    component: UsageSettings,
  },
  {
    id: 'connections',
    label: 'Third-party Connections',
    title: 'Third-party Connections',
    icon: faPlug,
    path: '/account-settings/connections',
    component: ConnectionSettings,
  },
  {
    id: 'saved-env-vars',
    label: 'Saved Variables',
    title: 'Saved Variables',
    icon: faLock,
    path: '/account-settings/saved-env-vars',
    component: SavedEnvVarSets,
  },
  {
    id: 'broker-connections',
    label: 'Broker Connections',
    title: 'Broker Connections',
    icon: faBuildingColumns,
    path: '/account-settings/broker-connections',
    component: BrokerConnectionsPage,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    title: 'Notifications',
    icon: faBell,
    path: '/account-settings/notifications',
    component: NotificationSettings,
  },
  {
    id: 'experiments',
    label: 'Experiments',
    title: 'Experiments',
    icon: faFlask,
    path: '/account-settings/experiments',
    component: ExperimentSettings,
    previewOnly: true,
  },
];

const sectionFromPath = (
  pathname: string,
  settingsSections: SettingsSection[],
): SettingsSectionId | null => {
  const section = pathname.split('/').filter(Boolean)[1];
  if (!section) return null;
  return settingsSections.some(({ id }) => id === section) ? (section as SettingsSectionId) : null;
};

const AccountSettingsMenuGrid = ({
  settingsSections,
}: {
  settingsSections: SettingsSection[];
}): React.ReactElement => (
  <section className="account-settings-menu-grid" aria-label="Account settings sections">
    {settingsSections.map((section) => (
      <Link key={section.id} className="account-settings-menu-card" to={section.path}>
        <FontAwesomeIcon icon={section.icon} className="account-settings-menu-card-icon" />
        <span>{section.label}</span>
      </Link>
    ))}
  </section>
);

const AccountSettings = ({
  contextualNavigation = false,
  experimentPreviewAvailable = isExperimentPreviewAvailable(),
}: AccountSettingsProps): React.ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const [themeMode] = useThemeMode();
  const settingsSections = SETTINGS_SECTIONS.filter(
    (section) => !section.previewOnly || experimentPreviewAvailable,
  );
  const activeSection = sectionFromPath(location.pathname, settingsSections);
  const activeSettingsSection = activeSection
    ? settingsSections.find((section) => section.id === activeSection) || null
    : null;
  const ActiveSection = activeSettingsSection?.component;
  const scopedThemeAttribute = applyThemeMode(themeMode);

  useEffect(() => {
    const canonicalPath = activeSettingsSection?.path;
    if (canonicalPath && location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
      return;
    }
    if (!canonicalPath && location.pathname !== '/account-settings') {
      navigate('/account-settings', { replace: true });
    }
  }, [activeSettingsSection, location.pathname, navigate]);

  return (
    <div className="account-settings-page bsui-scope fade-in" data-bs-theme={scopedThemeAttribute}>
      <Container>
        {activeSettingsSection ? (
          <>
            {!contextualNavigation ? (
              <nav className="account-settings-breadcrumbs" aria-label="Breadcrumb">
                <Link to="/account-settings">Account Settings</Link>
                <span aria-hidden="true">/</span>
                <span aria-current="page">{activeSettingsSection.title}</span>
              </nav>
            ) : null}

            <div
              className={`account-settings-layout${contextualNavigation ? ' account-settings-layout--contextual' : ''}`}
            >
              {!contextualNavigation ? (
                <div className="account-settings-sidebar">
                  <aside className="settings-nav-panel" aria-label="Account settings">
                    <Nav className="flex-column settings-nav">
                      {settingsSections.map((section) => (
                        <Nav.Link
                          key={section.id}
                          as={Link}
                          to={section.path}
                          className={`settings-nav-item ${activeSection === section.id ? 'active' : ''}`}
                        >
                          <FontAwesomeIcon icon={section.icon} className="settings-nav-icon" />
                          <span>{section.label}</span>
                        </Nav.Link>
                      ))}
                    </Nav>
                  </aside>
                </div>
              ) : null}

              <div className="account-settings-main">
                <section className="settings-content-panel">
                  {ActiveSection ? <ActiveSection /> : null}
                </section>
              </div>
            </div>
          </>
        ) : (
          <div className="account-settings-menu-page">
            <header className="account-settings-header">
              <h1>Account Settings</h1>
              <p className="settings-subpage-description account-settings-header-description">
                Manage profile, billing, integrations, and preferences.
              </p>
            </header>
            <AccountSettingsMenuGrid settingsSections={settingsSections} />
          </div>
        )}
      </Container>
    </div>
  );
};

export default AccountSettings;
